import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import { ChevronRight, RefreshCw } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { canViewReplacementOrders, canCreateReplacements } from "../../utils/roles";
import { fetchReplacementOrders, retryReplacementWhatsApp } from "../../api/ReplacementAPIs";
import { updateOrderStatus } from "../../api/OrderAPIs";

const STATUSES = ["All", "Confirmed", "Girabatcom", "Printlet", "Printing Hold", "TeePlus", "Printed", "Hold", "Incomplete"];
const ACTIONS = ["Girabatcom", "Printlet", "Printing Hold", "TeePlus", "Printed", "Hold", "Incomplete"];

const ReplacementOrdersPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authState } = useAuth();
  const canView = canViewReplacementOrders(authState.role);
  const canAct = canCreateReplacements(authState.role);
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState(null);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["replacementOrders", status, page],
    queryFn: () => fetchReplacementOrders({ status, page, pageSize: 20 }),
    enabled: canView,
  });

  if (!canView) return null;

  const items = data?.items || [];
  const total = data?.totalCount || 0;

  const changeStatus = async (orderId, nextStatus) => {
    setBusyId(orderId);
    try {
      await updateOrderStatus(orderId, nextStatus);
      toast.success(`تم تحديث #${orderId} إلى ${nextStatus}`);
      queryClient.invalidateQueries({ queryKey: ["replacementOrders"] });
    } catch (err) {
      toast.error(err.message || "فشل تحديث الحالة");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      <div className="max-w-6xl mx-auto space-y-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => navigate("/dashboard")} className="p-2 rounded-xl bg-white shadow">
              <ChevronRight size={22} />
            </button>
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-gray-800">طلبات الاستبدال</h1>
              <p className="text-sm text-gray-500">طلبات الاستبدال فقط، مع حالة التشغيل العادية</p>
            </div>
          </div>
          <button type="button" onClick={() => refetch()} className="px-4 py-2 rounded-xl bg-white border flex items-center gap-2">
            <RefreshCw size={16} className={isFetching ? "animate-spin" : ""} /> تحديث
          </button>
        </div>

        <div className="flex flex-wrap gap-2">
          {STATUSES.map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => { setStatus(item); setPage(0); }}
              className={`px-3 py-1.5 rounded-full text-sm font-bold ${status === item ? "bg-indigo-600 text-white" : "bg-white text-gray-700"}`}
            >
              {item}
            </button>
          ))}
        </div>

        {isLoading ? <p className="text-gray-500">جاري التحميل...</p> : (
          <div className="space-y-3">
            {items.length === 0 && <div className="bg-white rounded-2xl p-6 text-gray-500">لا توجد طلبات استبدال.</div>}
            {items.map((order) => (
              <article key={order.id || order.replacementOrderId} className="bg-white rounded-2xl shadow border border-white p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <h2 className="font-bold text-lg">#{order.replacementOrderId}</h2>
                    <p className="text-sm text-gray-600">{order.name} · {order.phoneNumber}</p>
                    <p className="text-sm text-gray-600">{order.governorate} {order.city ? `· ${order.city}` : ""}</p>
                  </div>
                  <div className="text-left">
                    <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-sm font-bold">{order.status}</span>
                    <p className="text-sm mt-1">الإجمالي {order.totalAmount}</p>
                  </div>
                </div>
                <p className="text-sm">
                  أصل #{order.originalOrderId}
                  {order.ticketId ? ` · تذكرة #${order.ticketId}` : " · بدون تذكرة"}
                  {order.problemReason ? ` · ${order.problemReason}` : ""}
                  · كمية {order.quantity} · بنود {order.lineCount}
                </p>
                {order.resolveWhatsAppStatus && (
                  <p className="text-sm text-gray-600">
                    واتساب الحل: {order.resolveWhatsAppStatus}
                    {canAct && order.resolveWhatsAppStatus === "Failed" && (
                      <button
                        type="button"
                        disabled={busyId === order.replacementOrderId}
                        onClick={async () => {
                          setBusyId(order.replacementOrderId);
                          try {
                            const result = await retryReplacementWhatsApp(order.replacementOrderId);
                            toast.success(result.status === "Sent" ? `تم إرسال واتساب للطلب #${order.replacementOrderId}` : `واتساب: ${result.status || "غير معروف"}`);
                            queryClient.invalidateQueries({ queryKey: ["replacementOrders"] });
                          } catch (err) {
                            toast.error(err.message || "فشل إعادة إرسال واتساب");
                          } finally {
                            setBusyId(null);
                          }
                        }}
                        className="mr-2 font-bold text-indigo-700 disabled:opacity-50"
                      >
                        إعادة الإرسال
                      </button>
                    )}
                  </p>
                )}
                <Link to={`/search-orders?orderId=${order.replacementOrderId}`} className="inline-block text-sm font-bold text-indigo-700">
                  فتح الطلب
                </Link>
                {canAct && (
                  <div className="flex flex-wrap gap-2">
                    {ACTIONS.map((action) => (
                      <button
                        key={action}
                        type="button"
                        disabled={busyId === order.replacementOrderId || order.status === action}
                        onClick={() => changeStatus(order.replacementOrderId, action)}
                        className="px-3 py-1.5 rounded-lg bg-slate-800 text-white text-xs disabled:opacity-40"
                      >
                        {action}
                      </button>
                    ))}
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between text-sm">
          <span>{total} طلب</span>
          <div className="flex gap-2">
            <button type="button" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="px-3 py-1 rounded-lg bg-white disabled:opacity-40">السابق</button>
            <button type="button" disabled={(page + 1) * 20 >= total} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 rounded-lg bg-white disabled:opacity-40">التالي</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReplacementOrdersPage;
